import type { Views } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

export type TimelineEvent = Views<'patient_timeline'>;
export type TrafficLight = 'red' | 'yellow' | 'green';

/**
 * Cabeçalho do perfil: reaproveita `doctor_patient_triage` filtrada por
 * paciente em vez de montar um SELECT novo em `patients`. A view já resolve
 * nome, telefone, estado, motivo, tempo no estado e última sincronização — e,
 * mais importante, já carrega o filtro `doctor_id = current_doctor_id()` por
 * dentro. Consultar `patients` direto aqui significaria reimplementar esse
 * escopo, e é exatamente aí que um vazamento nasce.
 */
export function usePatientHeader(patientId: string) {
  return useQuery({
    queryKey: ['patient-header', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('doctor_patient_triage')
        .select('*')
        .eq('patient_id', patientId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function usePatientTimeline(patientId: string) {
  return useQuery({
    queryKey: ['patient-timeline', patientId],
    queryFn: async (): Promise<TimelineEvent[]> => {
      const { data, error } = await supabase
        .from('patient_timeline')
        .select('*')
        .eq('patient_id', patientId)
        .order('occurred_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface MetricOption {
  metric_key: string;
  display_name: string;
  /** Unidade de ARMAZENAMENTO — é nela que o limiar da regra é gravado. */
  canonical_unit: string;
  /** Como escrever a unidade para o médico. Nula quando a canônica já serve. */
  display_unit: string | null;
}

/**
 * Unidade como o médico deve ler.
 *
 * Existe para as telas não exibirem o token de armazenamento cru — era assim
 * que a regra aparecia como "Saturação de oxigênio < 92 percent". Mora aqui e
 * não em cada tela porque a triagem, a regra e a notificação precisam dizer a
 * mesma coisa.
 *
 * Nunca implica escala diferente da canônica: o limiar é digitado e gravado na
 * canônica, então um rótulo que sugerisse outra unidade faria o médico
 * configurar um número e o motor comparar outro.
 */
export function unidadeLegivel(metric?: {
  canonical_unit: string;
  display_unit?: string | null;
}): string {
  if (!metric) return '';
  return metric.display_unit ?? metric.canonical_unit;
}

/**
 * As métricas oferecidas no seletor de tendência vêm do catálogo, não de uma
 * lista no código: o catálogo já é a fonte da verdade que o motor de alertas
 * (fase 5) vai usar para avaliar regras. Duas listas divergiriam, e a que
 * divergisse em silêncio seria a da UI.
 */
export function useMetricCatalog() {
  return useQuery({
    queryKey: ['metric-catalog'],
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<MetricOption[]> => {
      const { data, error } = await supabase
        .from('metric_catalog')
        .select('metric_key, display_name, canonical_unit, display_unit')
        .eq('active', true)
        .order('display_name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface TrendRow {
  bucket: string;
  min_value: number;
  max_value: number;
  avg_value: number;
  sample_count: number;
}

export function usePatientTrend(patientId: string, metricKey: string, days: number) {
  return useQuery({
    queryKey: ['patient-trend', patientId, metricKey, days],
    queryFn: async (): Promise<TrendRow[]> => {
      const { data, error } = await supabase.rpc('get_patient_trend', {
        p_patient_id: patientId,
        p_metric_key: metricKey,
        p_days: days,
      });
      if (error) throw error;
      return (data ?? []) as TrendRow[];
    },
  });
}

/**
 * Formato do resumo devolvido por `get_patient_overview`.
 *
 * Escrito à mão, não gerado: a RPC devolve `jsonb`, e o gerador do Supabase
 * não tem como enxergar a estrutura de dentro — para ele é `Json`. As seções
 * são anuláveis porque cada uma some quando o paciente não tem aquele dado no
 * período, e a tela precisa distinguir "zero" de "não medido".
 */
export interface PatientOverview {
  days: number;
  from: string;
  heart_rate: {
    avg: number;
    min: number;
    max: number;
    cv: number;
    resting: number | null;
    nocturnal: number | null;
    days: number;
    samples: number;
  } | null;
  /**
   * Frequência respiratória, do agregado diário.
   *
   * `nocturnal` e `avg` praticamente coincidem com Apple Watch — medido em 84
   * dias, 83% das amostras já são do sono e a diferença média é de 0,17 irpm.
   * `nocturnal_samples` existe para o card poder dizer isso em vez de fingir um
   * contraste que o dado não sustenta.
   *
   * Nula quando o período não tem nenhum dia agregado.
   */
  respiratory: {
    avg: number;
    min: number;
    max: number;
    nocturnal: number | null;
    days: number;
    samples: number;
    nocturnal_samples: number;
  } | null;
  weight: { avg: number; min: number; max: number; cv: number | null; count: number } | null;
  weight_last: { kg: number; at: string } | null;
  vo2max: { avg: number; min: number; max: number; count: number } | null;
  vo2max_last: { value: number; at: string } | null;
  active_energy: {
    avg_kcal: number;
    max_kcal: number;
    total_kcal: number;
    days: number;
  } | null;
  sleep: {
    /** Noites com estágio detalhado — só estas alimentam os campos abaixo. */
    nights: number;
    avg_total_s: number;
    avg_efficiency: number;
    avg_awakenings: number;
    stages: { deep: number; rem: number; core: number; awake: number };
    /**
     * Sessão de sono que EXISTE mas chegou sem estágio nenhum — achado real:
     * um paciente com Zepp teve 34 noites assim num período de 90 dias, por
     * troca de relógio ou mudança na origem. Sem este par de campos, essas
     * noites ficavam invisíveis (a view `sleep_nightly` as exclui de propósito).
     *
     * `total_s_without_stages` é TEMPO NA CAMA, não tempo dormido — sem
     * estágio não há como separar os dois. Nunca rotular como "dormiu".
     */
    nights_without_stages: number;
    total_s_without_stages: number;
  } | null;
  spo2: { avg: number; min: number; count: number; below_95: number } | null;
  hrv: Record<string, { avg: number; min: number; max: number; count: number } | undefined>;
  workouts: {
    count: number;
    duration_s: number;
    kcal: number;
    by_type: { type: string; count: number; duration_s: number }[];
  };
}

export function usePatientOverview(patientId: string, days: number) {
  return useQuery({
    queryKey: ['patient-overview', patientId, days],
    queryFn: async (): Promise<PatientOverview> => {
      const { data, error } = await supabase.rpc('get_patient_overview', {
        p_patient_id: patientId,
        p_days: days,
      });
      if (error) throw error;
      return data as unknown as PatientOverview;
    },
  });
}

export function useOverrideStatus(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ status, reason }: { status: TrafficLight; reason: string }) => {
      const { error } = await supabase.rpc('override_patient_status', {
        p_patient_id: patientId,
        p_new_status: status,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      // A triagem também muda quando o estado muda — invalidar só o perfil
      // deixaria a lista mostrando o estado antigo até o próximo refetch.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['patient-header', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['doctor-triage'] }),
      ]);
    },
  });
}

/**
 * Registra que o médico abriu o WhatsApp daquele paciente.
 *
 * O CONTEÚDO da conversa acontece fora do sistema e não é registrado — essa é
 * a limitação consciente do MVP (§4.2), e a UI diz isso ao médico. O que fica
 * auditado é só o ato de abrir o canal, que já responde "houve contato fora do
 * sistema em tal data". O enum de auditoria (`whatsapp_link_opened`) foi
 * criado na fase 1 justamente para isso.
 */
export function useLogWhatsApp(patientId: string) {
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('log_whatsapp_open', { p_patient_id: patientId });
      if (error) throw error;
    },
  });
}

/**
 * `wa.me` exige o telefone só com dígitos — o `+` do E.164 quebra o link.
 */
export function whatsappUrl(phoneE164: string, message: string): string {
  return `https://wa.me/${phoneE164.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
}

/**
 * O que o paciente está vendo no celular AGORA.
 *
 * Existe para que os números do painel e os do app sejam literalmente os
 * mesmos — não "o mesmo cálculo refeito igual", mas a mesma linha do banco.
 * A auditoria que originou isto encontrou três divergências: sono (3h14 no
 * painel contra 4h17 no app), FC lida de tabelas diferentes, e passos que o
 * médico simplesmente não via.
 *
 * `get_patient_overview` continua respondendo "como foi o período"; este
 * snapshot responde "como está agora". As duas perguntas são legítimas, e a
 * confusão entre elas é que produzia números discordantes.
 */
export interface LastNight {
  night: string;
  total_s: number;
  deep_s: number;
  rem_s: number;
  core_s: number;
  awake_s: number;
  in_bed_s: number;
  awakenings: number;
  efficiency: number | null;
  first_stage_at: string;
  last_stage_at: string;
}

export interface StepsToday {
  day: string;
  steps: number;
  distance_m: number | null;
}

export interface LastWeight {
  kg: number;
  at: string;
  previous_kg: number | null;
}

export interface LastHeartRate {
  day: string;
  resting: number | null;
  nocturnal: number | null;
  avg: number;
  min: number;
  max: number;
}

export interface PatientSnapshot {
  last_night: LastNight | null;
  steps_today: StepsToday | null;
  last_weight: LastWeight | null;
  last_heart_rate: LastHeartRate | null;
}

export function usePatientSnapshot(patientId: string) {
  return useQuery({
    queryKey: ['patient-snapshot', patientId],
    queryFn: async (): Promise<PatientSnapshot> => {
      const { data, error } = await supabase.rpc('get_patient_snapshot', {
        p_patient_id: patientId,
      });
      if (error) throw error;
      return data as unknown as PatientSnapshot;
    },
  });
}
